import {isLoaded} from 'Core/models/Async';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {getFormattedDate} from 'Quoting/utils/dateUtils';
import {getProductNameFromModel} from 'Quoting/utils/helpers';
import {VALUE_NOT_AVAILABLE, safeRenderValue} from 'Quoting/utils/utilities';
import {capitalizeSubString} from 'Shared/utilities/stringFormatHelpers';
import {head} from 'fp-ts/lib/Array';
import {getOrElse, map as optionMap} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {useQuoteField} from '../hooks/useHandleQuoteFieldChange';
import {GridHeader} from '../shared/GridHeader';

const quoteDetailsHeaderLabelMapping = {
  status: 'Status',
  product: 'Product',
  effectiveDate: 'Effective Date',
  transactionEffectiveDate: 'Transaction Effective Date',
  agency: 'Producer',
};

const quoteHeaderDetailsPropertyPaths = {
  status: 'state',
  product: '_modelName',
  effectiveDate: 'termDetails.termEffectiveDate',
  transactionEffectiveDate: 'termDetails.termEffectiveDate',
};

export const QuoteDetailsHeader = () => {
  const {quote, getQuoteFieldValue} = useQuoteField();
  const {premiumSplitSummary} = useSelector((state: ReducerState) => state.compensation);

  const agencyName = useMemo(
    () =>
      isLoaded(premiumSplitSummary) && Array.isArray(premiumSplitSummary.Data?.splitParts)
        ? pipe(
            premiumSplitSummary.Data.splitParts,
            splitParts =>
              splitParts.filter(sp => sp.premiumSplitDetails.some(p => p.broker.roleCd === 'AgentOfRecord')),
            sp => head(sp),
            optionMap(split => split.brokerName),
            getOrElse(() => VALUE_NOT_AVAILABLE)
          )
        : VALUE_NOT_AVAILABLE,
    [premiumSplitSummary]
  );

  const quoteHeaderDetails = useMemo(
    () => ({
      status: capitalizeSubString(
        safeRenderValue(getQuoteFieldValue<string>(quoteHeaderDetailsPropertyPaths.status)),
        0,
        1
      ),
      product: getProductNameFromModel(
        capitalizeSubString(
          safeRenderValue(getQuoteFieldValue<string>(quoteHeaderDetailsPropertyPaths.product)),
          0,
          2
        ) as A360ProductModel
      ),
      effectiveDate: getFormattedDate(getQuoteFieldValue<string>(quoteHeaderDetailsPropertyPaths.effectiveDate)),
      transactionEffectiveDate: getFormattedDate(
        getQuoteFieldValue<string>(quoteHeaderDetailsPropertyPaths.transactionEffectiveDate)
      ),
      agency: agencyName,
    }),
    [quote, premiumSplitSummary]
  );
  return <GridHeader data={quoteHeaderDetails} labelMapping={quoteDetailsHeaderLabelMapping} />;
};
